import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../services/supabase';
import { Cliente } from '../types/Cliente';

interface FiltroClientes {
  codCliente?: string;
  razaoSocial?: string;
  dataInicial?: string;
  dataFinal?: string;
}

// Função para converter data DD/MM/YYYY para YYYY-MM-DD
const convertDateFormat = (date: string) => {
  if (!date) return '';
  const [day, month, year] = date.split('/');
  return `${year}-${month}-${day}`;
};

export const useListaClientes = (filtros: FiltroClientes = {}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientesData, setClientesData] = useState<Cliente[]>([]);

  const fetchClientes = async () => {
    try {
      setLoading(true);
      setError(null);

      // Primeiro busca o usuário logado
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Busca o codempresa do usuário
      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('empresa')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.empresa) throw new Error('Empresa não encontrada');

      // Converte as datas para o formato do banco
      const dataInicialFormatada = convertDateFormat(filtros.dataInicial || '');
      const dataFinalFormatada = convertDateFormat(filtros.dataFinal || '');

      // Busca os clientes
      const { data, error } = await supabase
        .from('listasconsultaapi')
        .select('data')
        .eq('rotaapi', 'FINANCEIROListaClienteDeb')
        .eq('codempresa', userData.empresa)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data[0]?.data) {
        let clientesArray = Array.isArray(data[0].data) ? data[0].data : [];

        // Aplica os filtros de data
        if (dataInicialFormatada || dataFinalFormatada) {
          clientesArray = clientesArray.filter(cliente => {
            const dataMinima = cliente.D_MINDTVENC;
            const dataMaxima = cliente.D_MAXDTVENC;

            if (dataInicialFormatada && dataMaxima < dataInicialFormatada) {
              return false;
            }
            if (dataFinalFormatada && dataMinima > dataFinalFormatada) {
              return false;
            }
            return true;
          });
        }

        setClientesData(clientesArray);
      } else {
        setClientesData([]);
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Erro ao buscar clientes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientes();
  }, [filtros.dataInicial, filtros.dataFinal]); // Atualiza apenas quando as datas mudam

  const clientes = useMemo(() => {
    return clientesData.filter(cliente => {
      if (filtros.codCliente && !cliente.I_CODCLI.toString().includes(filtros.codCliente)) {
        return false;
      }
      if (filtros.razaoSocial && !cliente.S_CLIENTE.toLowerCase().includes(filtros.razaoSocial.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [clientesData, filtros.codCliente, filtros.razaoSocial]); // Filtra localmente código e razão social

  return { clientes, loading, error, refetch: fetchClientes };
};
